import { FormEvent, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@chakra-ui/react';
import {
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  type PaymentRequest,
  type PaymentRequestPaymentMethodEvent,
  type Stripe,
  type StripeElements,
  type StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import { Portal } from 'react-portal';
import { useHistory } from 'react-router-dom';
import { useBeforeUnload, useLockBodyScroll, useToggle } from 'react-use';

import { Button } from '@m/components-ui/button';
import { toaster } from '@m/components-ui/toaster';
import Configurator from '@m/config/Configurator';
import { TextToPayVisitView } from '@m/types/api';
import { asCents } from '@m/utils/asCents';
import {
  classifyPaymentFailure,
  isVisitPaymentDue,
  PAYMENT_IN_PROGRESS_POLL_INTERVAL_MS,
  PAYMENT_IN_PROGRESS_POLL_TIMEOUT_MS,
  type PaymentFailureKind,
} from '@m/utils/duplicatePaymentGuard';
import { formatGrpcToCurrency, getCurrencySymbol } from '@m/utils/grpc/formatGRPCDollar';
import { heapTrackProperties } from '@m/utils/heap';
import { isBool } from '@m/utils/isBool';
import { isPaymentAlreadySucceeded } from '@m/utils/stripeLoader';
import {
  ExpressCheckout,
  PayWithCard,
  TermsAndConditionsFooter,
  VisitInfo,
} from 'apps/pay/components';
import { MemberConversionFlow } from 'apps/pay/components/MemberConversionFlow/MemberConversionFlow';
import { PayHeader } from 'apps/pay/components/PayHeader/PayHeader';
import {
  PaymentOutcomeNotice,
  type PaymentOutcomeKind,
} from 'apps/pay/components/PaymentOutcomeNotice/PaymentOutcomeNotice';
import { SavePaymentCheckbox } from 'apps/pay/components/SavePaymentCheckbox/SavePaymentCheckbox';
import { useClosedVisitRedirect } from 'apps/pay/hooks';
import { useBeginPayment, useVisitDetails } from 'apps/pay/hooks/payment';
import { useBeginPaymentGate } from 'apps/pay/hooks/useBeginPaymentGate';
import { PaymentService } from 'apps/pay/services';
import { type PayAttemptOutcome, runPayAttempt } from 'apps/pay/utils/payAttempt';

const VehicleConfirmModal = lazy(() => import('apps/pay/screens/VehicleConfirmModal'));
const ServiceFeeModal = lazy(() => import('apps/pay/screens/ServiceFeeInfo'));

function VisitLandingPage({
  visit,
  visitCode,
  usePaymentElement,
  onHoldClientSecretChange,
}: {
  /**
   * Asks the route wrapper to keep the current client secret (and so this page's state) rather
   * than remount on a new one. Held while a pay attempt is in flight or a payment outcome is shown.
   */
  onHoldClientSecretChange?: (hold: boolean) => void;
  usePaymentElement: boolean;
  visit: TextToPayVisitView;
  visitCode: string;
}) {
  const history = useHistory();

  useClosedVisitRedirect(visit);

  const stripe = useStripe();
  const elements = useElements();
  const { mutateAsync: beginPayment } = useBeginPayment();
  // Rollout gate for the begin-payment flow. Off (or not yet known): the pre-existing behavior, which
  // confirms with Stripe straight away and never refetches the visit while idle.
  const beginPaymentEnabled = useBeginPaymentGate(visit.siteId);

  const [supportsPaymentRequest, setSupportsPaymentRequest] = useState<boolean | null>(null);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest>();

  const [renderVehicleConfirm, setRenderVehicleConfirm] = useState(false);
  const [showVehicleConfirm, setShowVehicleConfirm] = useState(false);

  const [renderServiceFeeInfo, setRenderServiceFeeInfo] = useState(false);
  const [showServiceFeeInfo, setShowServiceFeeInfo] = useState(false);
  const [locked, toggleLocked] = useToggle(false);
  useLockBodyScroll(locked);

  const [isPaymentElementComplete, setIsPaymentElementComplete] = useState(false);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

  // Terminal outcomes from the payment claim guard. Once set, no pay control is offered again:
  // `in_progress` waits for the competing payment to land, `already_paid` is final.
  const [paymentOutcome, setPaymentOutcome] = useState<PaymentOutcomeKind | null>(null);
  const [inProgressTimedOut, setInProgressTimedOut] = useState(false);
  const isPollingInProgress = paymentOutcome === 'in_progress' && !inProgressTimedOut;

  // Same cache entry the route wrapper reads; polling here refreshes the `visit` prop above, and
  // `refetchVisit` is the one reload used after begin-payment outcomes and claim-guard failures.
  const { refetch: refetchVisit } = useVisitDetails(visitCode, {
    refetchInterval: isPollingInProgress ? PAYMENT_IN_PROGRESS_POLL_INTERVAL_MS : false,
  });

  useEffect(() => {
    if (paymentOutcome === 'in_progress' && !isVisitPaymentDue(visit)) {
      setPaymentOutcome('already_paid');
    }
  }, [paymentOutcome, visit]);

  useEffect(() => {
    if (!isPollingInProgress) return undefined;
    const timeoutId = setTimeout(
      () => setInProgressTimedOut(true),
      PAYMENT_IN_PROGRESS_POLL_TIMEOUT_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [isPollingInProgress]);

  const [memberConversionCompleted, setMemberConversionCompleted] = useState(false);
  const [shouldLinkVehicle, setShouldLinkVehicle] = useState(false);

  // v2: User's choice for save-for-later checkbox (CA opt-in)
  // Default to API's defaultChoice, fallback to false for CA compliance safety
  // (if display=true but defaultChoice missing, safer to require explicit opt-in)
  const [saveForLaterChoice, setSaveForLaterChoice] = useState<boolean>(
    visit.saveForLater?.defaultChoice ?? false,
  );
  const showSaveForLaterCheckbox = visit.saveForLater?.display ?? false;
  const showVehicleLinkingScreen = visit.linkVehicle?.display ?? false;

  const itemizedAmounts = visit?.itemizedAmounts;

  // Guards against overlapping pay attempts (double tap, form submit plus wallet button) so
  // begin-payment and Stripe's confirmPayment are each called at most once per attempt. React
  // state lags a tap; the ref rejects a second submit that lands before the re-render.
  const attemptInFlightRef = useRef(false);

  // Released one render late, which only holds the secret a little longer. Acquiring goes through
  // acquireSubmitLock so a refetch landing in the same render cannot remount mid-attempt.
  const holdClientSecret = isSubmittingPayment || paymentOutcome !== null;
  useEffect(() => {
    onHoldClientSecretChange?.(holdClientSecret);
  }, [holdClientSecret, onHoldClientSecretChange]);
  // Defensive: release the hold if this page unmounts while holding it, so the route wrapper can
  // never be left pinned to a secret for a page that is gone.
  useEffect(() => () => onHoldClientSecretChange?.(false), [onHoldClientSecretChange]);

  // Keep the page current while idle. Only on the Payment Element path: the legacy token path builds
  // its Stripe PaymentRequest once from the initial amounts, so a refetched total would desync the
  // wallet sheet from the page. Never refetch mid-confirm / 3DS handling.
  useEffect(() => {
    if (!usePaymentElement || !beginPaymentEnabled || isSubmittingPayment) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible' && !attemptInFlightRef.current) {
        refetchVisit();
      }
    }, Configurator.TEXT_TO_PAY_VISIT_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [beginPaymentEnabled, isSubmittingPayment, refetchVisit, usePaymentElement]);

  // When the total changes after a refetch, pull the adjusted PaymentIntent amount into the Payment
  // Element and wallet sheets. Elements here is bound to the PaymentIntent via clientSecret, which is
  // what fetchUpdates() supports. Deferred while a pay attempt is in flight. The synced total is only
  // recorded once fetchUpdates() succeeds, so a failed sync is retried on the next run (every pay
  // attempt triggers one via isSubmittingPayment) instead of being skipped until the total moves again.
  const totalPrice = formatGrpcToCurrency(visit?.quote?.totalPrice);
  const totalCurrency = visit?.quote?.totalPrice?.currencyCode ?? 'USD';
  const lastSyncedTotalRef = useRef(totalPrice);
  useEffect(() => {
    if (!usePaymentElement || !beginPaymentEnabled || !elements || isSubmittingPayment) {
      return undefined;
    }
    if (lastSyncedTotalRef.current === totalPrice) {
      return undefined;
    }
    let cancelled = false;
    elements
      .fetchUpdates()
      .then(() => {
        if (!cancelled) {
          lastSyncedTotalRef.current = totalPrice;
        }
      })
      .catch(() => {
        // Left unsynced so the next run retries. begin-payment is the backstop: a finalized amount
        // that differs from the total on screen never reaches confirm.
      });
    return () => {
      cancelled = true;
    };
  }, [beginPaymentEnabled, elements, isSubmittingPayment, totalPrice, usePaymentElement]);

  // One pay attempt on the client-secret path. Gate on: begin-payment runs first and confirm only
  // follows a `ready` for the exact total on screen. Gate off: the pre-existing direct confirm.
  const attemptPayment = async (stripeClient: Stripe, stripeElements: StripeElements) => {
    const confirm = () =>
      stripeClient.confirmPayment({
        elements: stripeElements,
        confirmParams: {
          return_url: `${window.location.origin}/${visitCode}/complete`,
        },
        redirect: 'if_required',
      });

    if (!beginPaymentEnabled) {
      return { kind: 'confirmed' as const, result: await confirm() };
    }

    return runPayAttempt({
      beginPayment: () => beginPayment({ visitCode }),
      confirm,
      expected: { amountMinor: asCents(totalPrice), currency: totalCurrency },
    });
  };

  // Handles a pay attempt that stopped before confirmPayment. Resolves true if it was handled.
  const handleUnconfirmedAttempt = useCallback(
    async (outcome: PayAttemptOutcome<unknown>) => {
      if (outcome.kind === 'confirmed') {
        return false;
      }

      if (outcome.kind === 'beginFailed') {
        // A 423 means another payment holds the claim; a refusal on a visit that is no longer due
        // means it was paid elsewhere. Both are terminal: no pay control is offered again.
        const kind = await classifyPaymentFailure({
          error: outcome.error,
          refreshVisit: async () => (await refetchVisit()).data,
        });

        heapTrackProperties('memberless-payment/begin-payment:failed', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
          outcome: kind,
        });

        if (kind !== 'failed') {
          setPaymentOutcome(kind);
          return true;
        }

        toaster.create({
          description:
            outcome.error instanceof Error && outcome.error.message
              ? outcome.error.message
              : 'Your total changed or a payment is already in progress. Please review the updated total and try again.',
          type: 'error',
          duration: 8 * 1000,
        });
        return true;
      }

      // The visit changed under us (or was freed): reload it. A paid/free visit redirects to the
      // receipt via useClosedVisitRedirect; otherwise the page shows the new total and the
      // total-change effect refreshes Elements.
      refetchVisit();

      if (outcome.kind === 'noPaymentRequired') {
        toaster.create({
          description: 'No payment is required for this visit.',
          type: 'info',
          duration: 8 * 1000,
        });
      } else if (outcome.kind === 'amountChanged') {
        toaster.create({
          description: `Your total changed to ${getCurrencySymbol(outcome.begin.currency)}${(outcome.begin.amountMinor / 100).toFixed(2)}. Please review the updated total and try again.`,
          type: 'error',
          duration: 8 * 1000,
        });
      }
      return true;
    },
    [refetchVisit],
  );

  const openVehicleConfirm = useCallback(() => {
    toggleLocked(true);
    setRenderVehicleConfirm(true);
    setShowVehicleConfirm(true);

    heapTrackProperties('memberless-payment/not-my-vehicle:opened', {
      'timestamp(utc)': new Date().toUTCString(),
      timestamp: Date.now(),
      transactionId: visit.id,
      userId: visit.userId,
    });
  }, [toggleLocked, visit.id, visit.userId]);

  const closeVehicleConfirm = useCallback(
    ({ track }: { track: boolean } = { track: true }) => {
      setShowVehicleConfirm(false);
      toggleLocked(false);
      if (track) {
        heapTrackProperties('memberless-payment/not-my-vehicle:closed', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
          transactionId: visit.id,
          userId: visit.userId,
        });
      }
    },
    [toggleLocked, visit.id, visit.userId],
  );

  const closeCompleteVehicleConfirm = useCallback(() => {
    setRenderVehicleConfirm(false);
  }, []);

  const openServiceFeeInfo = useCallback(() => {
    toggleLocked(true);
    setRenderServiceFeeInfo(true);
    setShowServiceFeeInfo(true);
  }, [toggleLocked]);

  const closeServiceFeeInfo = useCallback(() => {
    setShowServiceFeeInfo(false);
    toggleLocked(false);
  }, [toggleLocked]);

  const closeCompleteServiceFeeInfo = useCallback(() => {
    setRenderServiceFeeInfo(false);
  }, []);

  const completeVisit = useCallback(() => {
    history.replace(`/${visitCode}/complete`);
  }, [history, visitCode]);

  /**
   * The server refused (or failed) to record a payment. A claim lock or an already paid visit is a
   * terminal outcome and is shown in place of the pay controls; anything else is a plain error
   * and the customer may try again.
   */
  const handlePaymentFailure = useCallback(
    async (error: unknown, heapEvent: string): Promise<PaymentFailureKind> => {
      const kind = await classifyPaymentFailure({
        error,
        refreshVisit: async () => (await refetchVisit()).data,
      });

      heapTrackProperties(heapEvent, {
        'timestamp(utc)': new Date().toUTCString(),
        timestamp: Date.now(),
        outcome: kind,
      });

      if (kind === 'failed') {
        toaster.create({
          description: 'There was an error processing your payment',
          type: 'error',
          duration: 8 * 1000,
        });
      } else {
        setPaymentOutcome(kind);
      }

      return kind;
    },
    [refetchVisit],
  );

  const handleCardPayment = useCallback(
    async (
      token: string,
      options: {
        isBrowserSavedCard?: boolean;
        vendorTransactionId?: string;
      } = {},
    ) => {
      try {
        const isBrowserSaved = isBool(options.isBrowserSavedCard)
          ? options.isBrowserSavedCard
          : false;

        const paymentPayload = {
          visitCode,
          token,
          isBrowserSavedCard: isBrowserSaved,
          saveForLater: showSaveForLaterCheckbox ? saveForLaterChoice : false,
          linkVehicle: shouldLinkVehicle,
          vendorTransactionId: options.vendorTransactionId,
        };

        await PaymentService.completePayment(paymentPayload);

        heapTrackProperties('memberless-payment/pay(card):complete', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
        });
        completeVisit();
      } catch (error) {
        await handlePaymentFailure(error, 'memberless-payment/pay(card):failed');
      }
    },
    [
      visitCode,
      shouldLinkVehicle,
      completeVisit,
      showSaveForLaterCheckbox,
      saveForLaterChoice,
      handlePaymentFailure,
    ],
  );

  const handleBrowserPayment = useCallback(
    async (e: PaymentRequestPaymentMethodEvent) => {
      const token = e.paymentMethod.id;
      try {
        const paymentPayload = {
          visitCode,
          token,
          isBrowserSavedCard: true,
          saveForLater: showSaveForLaterCheckbox ? saveForLaterChoice : false,
          linkVehicle: shouldLinkVehicle,
        };

        await PaymentService.completePayment(paymentPayload);

        e.complete('success');
        heapTrackProperties('memberless-payment/pay(managed):complete', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
        });
        completeVisit();
      } catch (error) {
        e.complete('fail');
        await handlePaymentFailure(error, 'memberless-payment/pay(managed):failed');
      }
    },
    [
      completeVisit,
      visitCode,
      shouldLinkVehicle,
      showSaveForLaterCheckbox,
      saveForLaterChoice,
      handlePaymentFailure,
    ],
  );

  const handleBeforeUnload = useCallback(() => {
    if (visit && visit.open) {
      heapTrackProperties('memberless-payment/landing:abandoned', {
        'timestamp(utc)': new Date().toUTCString(),
        timestamp: Date.now(),
      });
    }
    return false;
  }, [visit]);

  useBeforeUnload(handleBeforeUnload);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (visit) {
      // Wrapping in setTimeout so that this event is
      // not triggered if the page is to be redirected
      // to the receipt page
      timeoutId = setTimeout(() => {
        heapTrackProperties('memberless-payment/landing:viewed', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
        });
      }, 300);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [visit]);

  useEffect(() => {
    async function loadPaymentRequest() {
      if (itemizedAmounts && stripe && !paymentRequest) {
        const amount = asCents(itemizedAmounts.totalPriceAmount);
        const parkingCost = asCents(itemizedAmounts.price);

        const displayItems = [
          {
            label: 'Parking cost',
            amount: parkingCost,
          },
        ];

        if (itemizedAmounts.validationAmount) {
          displayItems.push({
            label: 'Validation',
            amount: asCents(itemizedAmounts.validationAmount),
          });
        }

        if (itemizedAmounts.taxAmount) {
          displayItems.push({
            label: 'Tax',
            amount: asCents(itemizedAmounts.taxAmount),
          });
        }

        if (itemizedAmounts.feeAmount) {
          displayItems.push({
            label: 'Service fee',
            amount: asCents(itemizedAmounts.feeAmount),
          });
        }

        const paymentReq = stripe.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: 'Metropolis Parking',
            amount,
          },
          displayItems,
          requestPayerName: true,
          requestPayerPhone: true,
        });

        try {
          const result = await paymentReq.canMakePayment();

          if (result) {
            setPaymentRequest(paymentReq);
            setSupportsPaymentRequest(true);
          } else {
            setSupportsPaymentRequest(false);
          }
        } catch (e) {
          setSupportsPaymentRequest(false);
        }

        paymentReq.on('paymentmethod', handleBrowserPayment);
      }
    }

    if (!usePaymentElement) loadPaymentRequest();
  }, [handleBrowserPayment, itemizedAmounts, paymentRequest, stripe, usePaymentElement]);

  /**
   * Single-flight guard shared by both Payment Element paths. Stripe's `confirmPayment` charges
   * the card, so a double tap must never reach it twice, and nothing may be submitted once the
   * claim guard has produced a terminal outcome.
   */
  const acquireSubmitLock = () => {
    if (attemptInFlightRef.current || paymentOutcome) return false;
    attemptInFlightRef.current = true;
    setIsSubmittingPayment(true);
    onHoldClientSecretChange?.(true);
    return true;
  };

  const releaseSubmitLock = () => {
    attemptInFlightRef.current = false;
    setIsSubmittingPayment(false);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements || !acquireSubmitLock()) {
      return;
    }

    try {
      const outcome = await attemptPayment(stripe, elements);

      if (outcome.kind !== 'confirmed') {
        await handleUnconfirmedAttempt(outcome);
        return;
      }

      const { paymentIntent, error } = outcome.result;

      if (error) {
        // Handle case where PaymentIntent already succeeded (e.g., retry after network failure)
        if (isPaymentAlreadySucceeded(error)) {
          // Payment already succeeded - treat as success
          await handleCardPayment(error.payment_intent.payment_method?.toString() || '', {
            isBrowserSavedCard: true,
            vendorTransactionId: error.payment_intent.id,
          });
          return;
        }

        toaster.create({
          description: error.message || 'There was an error processing your payment',
          type: 'error',
          duration: 8 * 1000,
        });
        // Decline after begin-payment: the bill stays frozen, reload the visit and let the consumer retry.
        if (beginPaymentEnabled) {
          refetchVisit();
        }
        return;
      }

      await handleCardPayment(paymentIntent.payment_method?.toString() || '', {
        isBrowserSavedCard: true,
        vendorTransactionId: paymentIntent.id,
      });
    } catch (error: unknown) {
      // Catch unexpected errors (network failures, etc.)
      toaster.create({
        description: 'An unexpected error occurred while processing your payment',
        type: 'error',
        duration: 8 * 1000,
      });
    } finally {
      releaseSubmitLock();
    }
  };

  // begin-payment runs here, in onConfirm, and not in onClick: the click event's resolve()/reject()
  // must be called within 1 second, which a network round trip cannot guarantee.
  const handleExpressCheckout = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) {
      toaster.create({
        description: 'Payment system is not ready. Please try again.',
        type: 'error',
        duration: 8 * 1000,
      });

      heapTrackProperties('Text to pay express checkout', {
        'timestamp(utc)': new Date().toUTCString(),
        timestamp: Date.now(),
        paymentMethod: 'unknown',
        status: 'failed',
        visitId: visit.id,
        error: 'Payment system not ready',
      });

      return;
    }

    if (!acquireSubmitLock()) {
      event.paymentFailed({ reason: 'fail' });
      return;
    }

    try {
      const outcome = await attemptPayment(stripe, elements);

      if (outcome.kind !== 'confirmed') {
        event.paymentFailed({ reason: 'fail' });
        await handleUnconfirmedAttempt(outcome);
        return;
      }

      const { paymentIntent, error } = outcome.result;

      if (error) {
        // Handle case where PaymentIntent already succeeded (e.g., retry after network failure)
        if (isPaymentAlreadySucceeded(error)) {
          // Payment already succeeded - treat as success
          const paymentMethod =
            typeof error.payment_intent.payment_method === 'object'
              ? error.payment_intent.payment_method
              : null;
          const paymentMethodType = paymentMethod?.type || 'unknown';

          heapTrackProperties('Text to pay express checkout', {
            'timestamp(utc)': new Date().toUTCString(),
            timestamp: Date.now(),
            paymentMethod: paymentMethodType,
            status: 'success',
            visitId: visit.id,
            paymentIntentId: error.payment_intent.id,
          });

          await handleCardPayment(error.payment_intent.payment_method?.toString() || '', {
            isBrowserSavedCard: true,
            vendorTransactionId: error.payment_intent.id,
          });
          return;
        }

        toaster.create({
          description: error.message || 'Express checkout payment failed',
          type: 'error',
          duration: 8 * 1000,
        });

        heapTrackProperties('Text to pay express checkout', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
          paymentMethod: 'unknown',
          status: 'failed',
          visitId: visit.id,
          error: error.message || 'Express checkout payment failed',
        });

        // Decline after begin-payment: the bill stays frozen, reload the visit and let the consumer retry.
        if (beginPaymentEnabled) {
          refetchVisit();
        }
        return;
      }

      const paymentMethod =
        typeof paymentIntent.payment_method === 'object' ? paymentIntent.payment_method : null;
      const paymentMethodType = paymentMethod?.type || 'unknown';

      // Track success
      heapTrackProperties('Text to pay express checkout', {
        'timestamp(utc)': new Date().toUTCString(),
        timestamp: Date.now(),
        paymentMethod: paymentMethodType,
        status: 'success',
        visitId: visit.id,
        paymentIntentId: paymentIntent.id,
      });

      await handleCardPayment(paymentIntent.payment_method?.toString() || '', {
        isBrowserSavedCard: true,
        vendorTransactionId: paymentIntent.id,
      });
    } catch (error: unknown) {
      // Catch unexpected errors (network failures, etc.)
      toaster.create({
        description: 'An unexpected error occurred during express checkout',
        type: 'error',
        duration: 8 * 1000,
      });
    } finally {
      releaseSubmitLock();
    }
  };

  const handleMemberConversionContinue = useCallback((linkVehicle: boolean) => {
    setShouldLinkVehicle(linkVehicle);
    setMemberConversionCompleted(true);
  }, []);

  const handleBack = useCallback(() => {
    setMemberConversionCompleted(false);
  }, []);

  if (showVehicleLinkingScreen && !memberConversionCompleted) {
    return (
      <MemberConversionFlow
        vehicle={visit.vehicle}
        onContinue={handleMemberConversionContinue}
        visitId={visit.id}
        userId={visit.userId}
      />
    );
  }

  return (
    <>
      {showVehicleLinkingScreen ? <PayHeader onBack={handleBack} /> : <PayHeader />}
      <VisitInfo
        visit={visit}
        onClickNotMyVehicle={openVehicleConfirm}
        onClickServiceFeeInfo={openServiceFeeInfo}
      />
      {paymentOutcome && (
        <Box width="100%" px={4}>
          <PaymentOutcomeNotice
            kind={paymentOutcome}
            timedOut={inProgressTimedOut}
            onContinue={completeVisit}
          />
        </Box>
      )}
      {!paymentOutcome && usePaymentElement && (
        <Box width="100%" mt={4} px={4}>
          <Box mb={4}>
            <ExpressCheckoutElement
              onConfirm={handleExpressCheckout}
              options={{ buttonHeight: 40 }}
            />
          </Box>
          <form onSubmit={handleSubmit}>
            <PaymentElement onChange={(e) => setIsPaymentElementComplete(e.complete)} />
            <SavePaymentCheckbox
              display={showSaveForLaterCheckbox}
              checked={saveForLaterChoice}
              onChange={setSaveForLaterChoice}
            />
            <Button
              variant="brand"
              type="submit"
              size="lg"
              width="100%"
              mt={4}
              disabled={!isPaymentElementComplete || isSubmittingPayment}
              loading={isSubmittingPayment}
            >
              {`Pay $${formatGrpcToCurrency(visit?.quote?.totalPrice)}`}
            </Button>
          </form>
        </Box>
      )}
      {!paymentOutcome && !usePaymentElement && (
        <>
          <ExpressCheckout
            supportsPaymentRequest={supportsPaymentRequest}
            stripePaymentRequest={paymentRequest}
          />
          <PayWithCard
            visit={visit}
            supportsPaymentRequest={supportsPaymentRequest}
            onCardTokenReceived={handleCardPayment}
          />
        </>
      )}

      {/* Global footer for all payment views */}
      <Box px={4} pb={4}>
        <TermsAndConditionsFooter />
      </Box>

      <Suspense fallback={null}>
        {renderVehicleConfirm && (
          <Portal>
            <VehicleConfirmModal
              show={showVehicleConfirm}
              onClose={closeVehicleConfirm}
              onCloseComplete={closeCompleteVehicleConfirm}
            />
          </Portal>
        )}
      </Suspense>
      <Suspense fallback={null}>
        {renderServiceFeeInfo && (
          <Portal>
            <ServiceFeeModal
              show={showServiceFeeInfo}
              onClose={closeServiceFeeInfo}
              onCloseComplete={closeCompleteServiceFeeInfo}
            />
          </Portal>
        )}
      </Suspense>
    </>
  );
}

export default VisitLandingPage;
